// Liste von Coaching-Bookings (upcoming oder past).

import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import type { CoachingBooking } from '@/lib/coaching-api';

interface BookingListProps {
  title: string;
  bookings: ReadonlyArray<CoachingBooking>;
  emptyText: string;
}

const STATUS_ICON = {
  scheduled: { Icon: Clock, tone: 'text-amber-300' },
  completed: { Icon: CheckCircle2, tone: 'text-emerald-300' },
  no_show: { Icon: XCircle, tone: 'text-rose-300' },
  canceled: { Icon: XCircle, tone: 'text-neutral-500' },
} as const;

const STATUS_LABEL: Record<CoachingBooking['status'], string> = {
  scheduled: 'bestaetigt',
  completed: 'abgeschlossen',
  no_show: 'nicht erschienen',
  canceled: 'storniert',
};

export function BookingList({ title, bookings, emptyText }: BookingListProps) {
  if (bookings.length === 0) {
    return (
      <section className="glass-strong rounded-2xl p-4">
        <h3 className="mb-2 text-[12px] font-bold uppercase tracking-wider text-neutral-400">
          {title}
        </h3>
        <p className="text-[11px] italic text-neutral-500">{emptyText}</p>
      </section>
    );
  }
  return (
    <section className="glass-strong rounded-2xl p-4">
      <h3 className="mb-2 text-[12px] font-bold uppercase tracking-wider text-neutral-400">
        {title}
      </h3>
      <ul className="space-y-1.5">
        {bookings.map((b) => {
          const ico = STATUS_ICON[b.status];
          const d = new Date(b.scheduled_at);
          const date = d.toLocaleDateString('de-DE', {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
          });
          const time = d.toLocaleTimeString('de-DE', {
            timeZone: 'Europe/Berlin',
            hour: '2-digit',
            minute: '2-digit',
          });
          return (
            <li
              key={b.id}
              className="flex items-center justify-between rounded-md border border-white/5 bg-white/[0.02] px-3 py-1.5 text-[12px]"
            >
              <div className="flex items-center gap-2">
                <ico.Icon className={`size-3.5 ${ico.tone}`} />
                <span className="text-neutral-100 tabular">
                  {date} · {time}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-neutral-500">
                <span>{b.duration_min} min</span>
                <span className={ico.tone}>· {STATUS_LABEL[b.status]}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
